/**
 * Asset Name Builder — Live Tokenizer Edition.
 *
 * A smart asset name input that assigns words to fields based on the
 * org's configurable naming convention (e.g., Year Make Model Variant
 * Category) **as the user types**, with real-time badge updates and
 * dropdown suggestions from org settings.
 *
 * Two modes:
 * - **Free-form** (default): Single text input with live badges below.
 *   Words are assigned to slots following the naming convention order.
 *   Dropdowns show matching known values for the active slot.
 * - **Structured**: Individual ComboSelect fields for each active
 *   component with autocomplete from org settings and inline creation.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Wand2, PenLine, Check, Pencil } from "lucide-react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useOrgConfig } from "@/features/settings/api";
import ComboSelect from "@/components/ui/ComboSelect";
import type { ComboOption } from "@/components/ui/ComboSelect";
import { toast } from "@/stores/toastStore";
import type { NamingConvention, NamingComponent } from "@/types/settings";

interface AssetNameBuilderProps {
  value: string;
  onChange: (name: string) => void;
  category?: string;
  onFieldsChange?: (fields: {
    year?: string;
    make?: string;
    model?: string;
    variant?: string;
  }) => void;
  onCategoryChange?: (category: string) => void;
  onCustomFieldsChange?: (fields: Record<string, string>) => void;
  initialYear?: string;
  initialMake?: string;
  initialModel?: string;
  initialVariant?: string;
  initialCustomFields?: Record<string, string>;
  placeholder?: string;
  defaultStructured?: boolean;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Slot assignment types
// ---------------------------------------------------------------------------

type SlotName = string;

interface SlotAssignment {
  slot: SlotName;
  value: string;
}

const BUILTIN_SLOT_COLORS: Record<string, string> = {
  year: "micro-badge-blue",
  make: "micro-badge-violet",
  model: "micro-badge-green",
  variant: "micro-badge-rose",
  category: "micro-badge-amber",
};

const CUSTOM_SLOT_COLORS = [
  "micro-badge-cyan",
  "micro-badge-pink",
  "micro-badge-orange",
  "micro-badge-teal",
  "micro-badge-indigo",
];

const BUILTIN_SLOT_LABELS: Record<string, string> = {
  year: "Year",
  make: "Make",
  model: "Model",
  variant: "Variant",
  category: "Category",
};

const BUILTIN_KEYS: Set<string> = new Set([
  "year",
  "make",
  "model",
  "variant",
  "category",
]);

/** Get the display color class for a slot. */
function getSlotColor(key: string, customIndex?: number): string {
  if (BUILTIN_SLOT_COLORS[key]) return BUILTIN_SLOT_COLORS[key]!;
  const idx = customIndex ?? 0;
  return CUSTOM_SLOT_COLORS[idx % CUSTOM_SLOT_COLORS.length]!;
}

/** Get the display label for a slot from the convention. */
function getSlotLabel(key: string, convention: NamingConvention): string {
  const comp = convention.components.find((c) => c.key === key);
  if (comp?.label) return comp.label;
  if (BUILTIN_SLOT_LABELS[key]) return BUILTIN_SLOT_LABELS[key]!;
  return key;
}

/** Extract plain keys from convention components. */
function getComponentKeys(convention: NamingConvention): string[] {
  return convention.components.map((c) => c.key);
}

/** Get the index of a custom component within all custom components. */
function getCustomIndex(key: string, convention: NamingConvention): number {
  const customs = convention.components.filter((c) => !c.builtin);
  return customs.findIndex((c) => c.key === key);
}

const DEFAULT_CONVENTION: NamingConvention = {
  components: [
    { key: "year", builtin: true },
    { key: "make", builtin: true },
    { key: "model", builtin: true },
    { key: "category", builtin: true },
  ],
  separator: " ",
};

// ---------------------------------------------------------------------------
// Live tokenizer — assigns words to slots on every keystroke
//
// Processes convention components in order rather than using a fixed
// year→make→model→variant→category pipeline. This allows custom components
// to be interspersed with built-ins in any position.
//
// Strategy:
//   1. Identify tail-anchored components (variant/category at the end).
//   2. Forward-match head components in convention order.
//   3. "model" is the greedy component — absorbs all remaining middle tokens.
//   4. Tail components match from the end (known-value first, positional fallback).
// ---------------------------------------------------------------------------

/**
 * Try to match a single component from the front of the token list.
 * Returns the assignment and number of tokens consumed, or null if
 * the component didn't match (e.g. year regex failed).
 */
function matchForward(
  comp: NamingComponent,
  tokens: string[],
  idx: number,
  bound: number,
  knownMakes: string[],
): { assignment: SlotAssignment; count: number } | null {
  if (idx >= bound) return null;

  if (comp.key === "year" && comp.builtin) {
    if (/^(19|20)\d{2}$/.test(tokens[idx]!)) {
      return { assignment: { slot: "year", value: tokens[idx]! }, count: 1 };
    }
    return null;
  }

  if (comp.key === "make" && comp.builtin) {
    const lowerMakes = knownMakes.map((m) => m?.toLowerCase() ?? "");
    for (let len = Math.min(3, bound - idx); len >= 2; len--) {
      const candidate = tokens.slice(idx, idx + len).join(" ");
      const matchIdx = lowerMakes.indexOf(candidate.toLowerCase());
      if (matchIdx >= 0) {
        return {
          assignment: { slot: "make", value: knownMakes[matchIdx]! },
          count: len,
        };
      }
    }
    return { assignment: { slot: "make", value: tokens[idx]! }, count: 1 };
  }

  if (!comp.builtin && comp.input_type === "select" && comp.options?.length) {
    const lowerOpts: Record<string, string> = {};
    for (const o of comp.options) lowerOpts[o.toLowerCase()] = o;
    for (let len = Math.min(2, bound - idx); len >= 1; len--) {
      const candidate = tokens
        .slice(idx, idx + len)
        .join(" ")
        .toLowerCase();
      if (lowerOpts[candidate]) {
        return {
          assignment: { slot: comp.key, value: lowerOpts[candidate]! },
          count: len,
        };
      }
    }
    return { assignment: { slot: comp.key, value: tokens[idx]! }, count: 1 };
  }

  return { assignment: { slot: comp.key, value: tokens[idx]! }, count: 1 };
}

function assignTokensToSlots(
  text: string,
  knownMakes: string[],
  knownVariants: string[],
  categoryLabels: string[],
  convention: NamingConvention,
): SlotAssignment[] {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const components = convention.components;
  if (components.length === 0) return [];

  // ── Identify tail-anchored components ──
  // variant and category are tail-anchored when they form a contiguous
  // group at the end of the convention (they match from the end of the
  // token list rather than positionally from the front).
  const TAIL_KEYS = new Set(["variant", "category"]);
  let tailDivide = components.length;
  for (let i = components.length - 1; i >= 0; i--) {
    if (components[i]!.builtin && TAIL_KEYS.has(components[i]!.key)) {
      tailDivide = i;
    } else {
      break;
    }
  }
  const tailComps = components.slice(tailDivide);
  const headComps = components.slice(0, tailDivide);

  // Find "model" (the greedy component) within the head section.
  const modelLocalIdx = headComps.findIndex(
    (c) => c.key === "model" && c.builtin,
  );
  const preModel =
    modelLocalIdx >= 0 ? headComps.slice(0, modelLocalIdx) : headComps;
  const postModel =
    modelLocalIdx >= 0 ? headComps.slice(modelLocalIdx + 1) : [];

  // ── Phase 1: Forward-match pre-model components ──
  let fwd = 0;
  const assignments: SlotAssignment[] = [];

  for (const comp of preModel) {
    if (fwd >= tokens.length) break;
    const result = matchForward(comp, tokens, fwd, tokens.length, knownMakes);
    if (result) {
      assignments.push(result.assignment);
      fwd += result.count;
    }
  }

  // ── Phase 2: Match tail components from the end ──
  let tailBound = tokens.length;
  const tailAssignments: SlotAssignment[] = [];

  // First pass: try known-value matches (reverse convention order).
  let anyTailMatched = false;
  for (let i = tailComps.length - 1; i >= 0; i--) {
    const comp = tailComps[i]!;
    if (tailBound <= fwd) break;

    const knownValues =
      comp.key === "category"
        ? categoryLabels
        : comp.key === "variant"
          ? knownVariants
          : [];

    if (knownValues.length > 0) {
      const lower = knownValues.map((v) => v?.toLowerCase() ?? "");
      for (let len = Math.min(2, tailBound - fwd); len >= 1; len--) {
        const candidate = tokens.slice(tailBound - len, tailBound).join(" ");
        const matchIdx = lower.indexOf(candidate.toLowerCase());
        if (matchIdx >= 0) {
          tailAssignments.unshift({
            slot: comp.key,
            value: knownValues[matchIdx]!,
          });
          tailBound -= len;
          anyTailMatched = true;
          break;
        }
      }
    }
  }

  // Positional fallback: if no known values matched, reserve tokens from
  // the end for tail slots — but keep enough for model + post-model components.
  if (!anyTailMatched && tailComps.length > 0) {
    const neededForMiddle = (modelLocalIdx >= 0 ? 1 : 0) + postModel.length;
    const available = tailBound - fwd - neededForMiddle;
    const maxTail = Math.min(tailComps.length, Math.max(0, available));

    if (maxTail > 0) {
      tailBound -= maxTail;
      for (let i = 0; i < maxTail; i++) {
        tailAssignments.push({
          slot: tailComps[i]!.key,
          value: tokens[tailBound + i]!,
        });
      }
    }
  }

  // ── Phase 3: Assign model (greedy) and post-model components ──
  if (modelLocalIdx >= 0) {
    const modelEnd = Math.max(fwd, tailBound - postModel.length);

    if (fwd < modelEnd) {
      assignments.push({
        slot: "model",
        value: tokens.slice(fwd, modelEnd).join(" "),
      });
    }
    fwd = modelEnd;

    for (const comp of postModel) {
      if (fwd >= tailBound) break;
      const result = matchForward(comp, tokens, fwd, tailBound, knownMakes);
      if (result) {
        assignments.push(result.assignment);
        fwd += result.count;
      }
    }
  }

  // ── Phase 4: Append tail assignments ──
  assignments.push(...tailAssignments);

  return assignments;
}

/** Get suggestions for the active slot. */
function getSuggestions(
  activeSlot: SlotName,
  partialText: string,
  knownMakes: string[],
  knownModels: Array<string | { make: string; model: string }>,
  knownVariants: string[],
  categoryLabels: string[],
  convention: NamingConvention,
): string[] {
  if (!partialText) return [];
  const q = partialText.toLowerCase();

  switch (activeSlot) {
    case "make":
      return knownMakes.filter((m) => m?.toLowerCase().includes(q)).slice(0, 8);
    case "model": {
      const models =
        Array.isArray(knownModels) && knownModels.length > 0
          ? typeof knownModels[0] === "string"
            ? (knownModels as unknown as string[])
            : (knownModels as unknown as Array<{ model: string }>).map(
                (m) => m.model,
              )
          : [];
      return models.filter((m) => m && m.toLowerCase().includes(q)).slice(0, 8);
    }
    case "variant":
      return knownVariants
        .filter((v) => v?.toLowerCase().includes(q))
        .slice(0, 8);
    case "category":
      return categoryLabels
        .filter((c) => c?.toLowerCase().includes(q))
        .slice(0, 8);
    default: {
      // Custom component with select options.
      const comp = convention.components.find((c) => c.key === activeSlot);
      if (comp?.options) {
        return comp.options
          .filter((o) => o.toLowerCase().includes(q))
          .slice(0, 8);
      }
      return [];
    }
  }
}

/** Determine which slot the user is currently typing into. */
function getActiveSlot(
  assignments: SlotAssignment[],
  overrides: Partial<Record<string, string>>,
  convention: NamingConvention,
): SlotName {
  const keys = getComponentKeys(convention);

  for (const slot of keys) {
    const isAssigned = assignments.some((a) => a.slot === slot);
    const override = overrides[slot];
    if (override === "__merged__") return slot;
    if (!isAssigned && override === undefined) return slot;
  }
  return keys[keys.length - 1] || "category";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCategoryValue(
  categories: Array<{
    value: string;
    label: string;
    children?: Array<{ value: string; label: string }>;
  }>,
  label: string,
): string | null {
  const lower = label.toLowerCase();
  for (const cat of categories) {
    if (cat.label.toLowerCase() === lower) return cat.value;
    if (cat.children) {
      for (const child of cat.children) {
        if (child.label.toLowerCase() === lower) return child.value;
      }
    }
  }
  return null;
}

function getCategoryLabel(
  categories: Array<{
    value: string;
    label: string;
    children?: Array<{ value: string; label: string; children?: unknown[] }>;
  }>,
  value: string,
): string {
  for (const cat of categories) {
    if (cat.value === value) return cat.label;
    if (cat.children) {
      for (const child of cat.children as typeof categories) {
        const found = getCategoryLabel([child], value);
        if (found !== value) return found;
      }
    }
  }
  return value;
}

// ---------------------------------------------------------------------------
// TokenizedNameInput — live badges + dropdown
// ---------------------------------------------------------------------------

function TokenizedNameInput({
  value,
  onChange,
  knownMakes,
  knownModels,
  knownVariants,
  categoryLabels,
  allCategories,
  convention,
  onApply,
  placeholder,
  inputClass,
}: {
  value: string;
  onChange: (v: string) => void;
  knownMakes: string[];
  knownModels: Array<
    string | { make: string; model: string; category?: string }
  >;
  knownVariants: string[];
  categoryLabels: string[];
  allCategories: Array<{
    value: string;
    label: string;
    children?: Array<{ value: string; label: string }>;
  }>;
  convention: NamingConvention;
  onApply: (fields: {
    year?: string;
    make?: string;
    model?: string;
    variant?: string;
    category?: string;
    customFields?: Record<string, string>;
  }) => void;
  placeholder: string;
  inputClass: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLInputElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [applied, setApplied] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [editingSlot, setEditingSlot] = useState<SlotName | null>(null);
  const [editValue, setEditValue] = useState("");
  const [localMakes, setLocalMakes] = useState<string[]>([]);
  const effectiveMakes = useMemo(
    () => [...knownMakes, ...localMakes.filter((m) => !knownMakes.includes(m))],
    [knownMakes, localMakes],
  );

  useEffect(() => {
    if (editingSlot && editRef.current) editRef.current.focus();
  }, [editingSlot]);

  const assignments = useMemo(
    () =>
      assignTokensToSlots(
        value,
        effectiveMakes,
        knownVariants,
        categoryLabels,
        convention,
      ),
    [value, effectiveMakes, knownVariants, categoryLabels, convention],
  );

  const tokens = value.trim().split(/\s+/).filter(Boolean);
  const lastToken = tokens.length > 0 ? (tokens[tokens.length - 1] ?? "") : "";
  const endsWithSpace = value.endsWith(" ") && value.trim().length > 0;
  const activeSlot = getActiveSlot(assignments, {}, convention);

  const partialForSuggestion = endsWithSpace ? "" : lastToken;
  const suggestions = useMemo(
    () =>
      getSuggestions(
        activeSlot,
        partialForSuggestion,
        effectiveMakes,
        knownModels,
        knownVariants,
        categoryLabels,
        convention,
      ),
    [
      activeSlot,
      partialForSuggestion,
      effectiveMakes,
      knownModels,
      knownVariants,
      categoryLabels,
      convention,
    ],
  );

  useEffect(() => {
    if (suggestions.length > 0 && partialForSuggestion && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
      setShowDropdown(true);
      setHighlightIdx(0);
    } else {
      setShowDropdown(false);
    }
  }, [suggestions.length, partialForSuggestion]);

  const selectSuggestion = useCallback(
    (suggestion: string) => {
      const currentTokens = value.trim().split(/\s+/).filter(Boolean);

      const sugWords = suggestion.split(/\s+/);
      let matchCount = 0;
      for (
        let i = 1;
        i <= Math.min(sugWords.length, currentTokens.length);
        i++
      ) {
        const tail = currentTokens.slice(-i).join(" ").toLowerCase();
        const sugStart = sugWords.slice(0, i).join(" ").toLowerCase();
        if (sugStart.startsWith(tail) || tail.startsWith(sugStart)) {
          matchCount = i;
        }
      }

      const prefix = currentTokens.slice(
        0,
        currentTokens.length - Math.max(matchCount, 1),
      );
      const newValue = [...prefix, suggestion].join(" ") + " ";
      onChange(newValue);
      setShowDropdown(false);
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (suggestions[highlightIdx]) {
          e.preventDefault();
          selectSuggestion(suggestions[highlightIdx]);
        }
      } else if (e.key === "Escape") {
        setShowDropdown(false);
      }
    }
  };

  const handleApply = () => {
    const fields: Record<string, string | undefined> = {};
    const customFields: Record<string, string> = {};
    for (const a of assignments) {
      if (a.slot === "category") {
        fields.category = getCategoryValue(allCategories, a.value) || a.value;
      } else if (BUILTIN_KEYS.has(a.slot)) {
        fields[a.slot] = a.value;
      } else {
        customFields[a.slot] = a.value;
      }
    }

    onApply({
      year: fields.year,
      make: fields.make,
      model: fields.model,
      variant: fields.variant,
      category: fields.category,
      customFields:
        Object.keys(customFields).length > 0 ? customFields : undefined,
    });
    setApplied(true);
    setTimeout(() => setApplied(false), 2000);
  };

  const hasAssignments = assignments.length > 0;

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        onFocus={() => {
          if (suggestions.length > 0 && partialForSuggestion)
            setShowDropdown(true);
        }}
        placeholder={placeholder}
        className={`block w-full ${inputClass}`}
      />

      {/* Live badges — editable with merge buttons */}
      {hasAssignments && (
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          {assignments.map((a, i) => (
            <div key={a.slot} className="flex items-center gap-1">
              {/* Merge button — absorb this slot's value into the previous slot */}
              {i > 0 && assignments[i - 1] && (
                <button
                  type="button"
                  onClick={() => {
                    const prev = assignments[i - 1]!;
                    const merged = `${prev.value} ${a.value}`;
                    if (prev.slot === "make" || a.slot === "make") {
                      setLocalMakes((m) =>
                        m.includes(merged) ? m : [...m, merged],
                      );
                    }
                  }}
                  className="h-4 w-4 flex items-center justify-center rounded-full text-[8px] text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)] hover:bg-[var(--fill-info)] transition-colors"
                  title={`Merge into ${getSlotLabel(assignments[i - 1]!.slot, convention)}`}
                >
                  ←
                </button>
              )}
              {/* Badge — click to edit */}
              {editingSlot === a.slot ? (
                <input
                  ref={editRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (a.slot === "make" && editValue !== a.value) {
                        setLocalMakes((m) =>
                          m.includes(editValue) ? m : [...m, editValue],
                        );
                      }
                      setEditingSlot(null);
                    }
                    if (e.key === "Escape") setEditingSlot(null);
                  }}
                  onBlur={() => {
                    if (a.slot === "make" && editValue !== a.value) {
                      setLocalMakes((m) =>
                        m.includes(editValue) ? m : [...m, editValue],
                      );
                    }
                    setEditingSlot(null);
                  }}
                  className="h-6 w-28 rounded-full border border-[var(--border-focus)] bg-[var(--surface-base)] px-2 text-[10px] font-semibold text-[var(--text-primary)] focus:outline-none shadow-[var(--focus-ring)]"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingSlot(a.slot);
                    setEditValue(a.value);
                  }}
                  className={`micro-badge ${getSlotColor(a.slot, getCustomIndex(a.slot, convention))} cursor-pointer hover:opacity-80 transition-opacity`}
                  title={`Click to edit ${getSlotLabel(a.slot, convention)}`}
                >
                  {getSlotLabel(a.slot, convention)}: {a.value}
                  <Pencil className="h-2.5 w-2.5 ml-0.5 opacity-50" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleApply}
            className={`micro-badge cursor-pointer transition-all ${
              applied
                ? "micro-badge-green"
                : "bg-[var(--color-interactive-primary)] text-white hover:opacity-90"
            }`}
            title="Apply parsed values to fields"
          >
            <Check className="h-3 w-3" />
            {applied ? "Applied" : "Apply"}
          </button>
        </div>
      )}

      {/* Active slot indicator */}
      {value && !hasAssignments && (
        <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
          Enter components in order:{" "}
          {getComponentKeys(convention)
            .map((k) => getSlotLabel(k, convention))
            .join(", ")}
        </p>
      )}

      {/* Dropdown suggestions (portal) */}
      {showDropdown &&
        suggestions.length > 0 &&
        createPortal(
          <div
            className="fixed z-[9999] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)]"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="px-2 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--color-border)]">
              {getSlotLabel(activeSlot, convention)} suggestions
            </div>
            <div className="max-h-40 overflow-y-auto">
              {suggestions.map((s, i) => (
                <div
                  key={s}
                  onClick={() => selectSuggestion(s)}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                    i === highlightIdx
                      ? "bg-[var(--color-interactive-primary)]/[0.06]"
                      : "hover:bg-[var(--surface-sunken)]"
                  }`}
                >
                  {s}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AssetNameBuilder({
  value,
  onChange,
  category,
  onFieldsChange,
  onCategoryChange,
  onCustomFieldsChange,
  initialYear,
  initialMake,
  initialModel,
  initialVariant,
  initialCustomFields,
  placeholder,
  defaultStructured = false,
  compact = false,
}: AssetNameBuilderProps) {
  const { data: config } = useOrgConfig();
  const [isStructured, setIsStructured] = useState(defaultStructured);
  const [year, setYear] = useState(initialYear || "");
  const [make, setMake] = useState(initialMake || "");
  const [model, setModel] = useState(initialModel || "");
  const [variant, setVariant] = useState(initialVariant || "");
  const [initialized, setInitialized] = useState(false);

  const convention: NamingConvention = useMemo(
    () => config?.products?.naming_convention || DEFAULT_CONVENTION,
    [config],
  );

  const makeObjects = useMemo(() => config?.products?.makes || [], [config]);
  const knownMakes = useMemo(
    () =>
      makeObjects
        .map((m) => (typeof m === "string" ? m : m.name))
        .filter((n): n is string => !!n),
    [makeObjects],
  );
  const knownModels = useMemo(() => config?.products?.models || [], [config]);
  const knownVariants = useMemo(() => {
    const variants = config?.products?.variants || [];
    return variants
      .map((v) => (typeof v === "string" ? v : v.name))
      .filter((n): n is string => !!n);
  }, [config]);

  const allCategories = useMemo(() => {
    const prodCats = config?.products?.asset_categories;
    const invCats = config?.inventory?.asset_categories;
    return prodCats && prodCats.length > 0
      ? prodCats
      : invCats && invCats.length > 0
        ? invCats
        : [];
  }, [config]);

  const categoryLabels = useMemo(
    () => allCategories.map((c) => c.label).filter((l): l is string => !!l),
    [allCategories],
  );

  const categoryLabel = useMemo(() => {
    if (!category) return "";
    return getCategoryLabel(allCategories, category);
  }, [category, allCategories]);

  const makeOptions: ComboOption[] = useMemo(
    () => knownMakes.map((m: string) => ({ value: m, label: m })),
    [knownMakes],
  );

  const modelOptions: ComboOption[] = useMemo(() => {
    if (Array.isArray(knownModels) && knownModels.length > 0) {
      if (typeof knownModels[0] === "string") {
        return (knownModels as unknown as string[]).map((m) => ({
          value: m,
          label: m,
        }));
      }
      const typed = knownModels as unknown as Array<{
        make: string;
        model: string;
      }>;
      const filtered = make
        ? typed.filter((m) => m.make?.toLowerCase() === make.toLowerCase())
        : typed;
      return filtered
        .filter((m) => m.model)
        .map((m) => ({
          value: m.model,
          label: m.model,
          sublabel: m.make,
        }));
    }
    return [];
  }, [knownModels, make]);

  const variantOptions: ComboOption[] = useMemo(
    () => knownVariants.map((v) => ({ value: v, label: v })),
    [knownVariants],
  );

  // Build the default placeholder from the convention.
  const defaultPlaceholder = useMemo(() => {
    const builtinExamples: Record<string, string> = {
      year: "2024",
      make: "John Deere",
      model: "6120M",
      variant: "Premium",
      category: "Tractor",
    };
    const parts = convention.components
      .map((c) => {
        if (c.builtin && builtinExamples[c.key]) return builtinExamples[c.key];
        if (c.options && c.options.length > 0) return c.options[0];
        return c.label || c.key;
      })
      .filter(Boolean);
    return `e.g., ${parts.join(" ")}`;
  }, [convention]);

  const qc = useQueryClient();

  /**
   * Save new make, model, variant, and/or category to org settings in a
   * single call.
   */
  const saveNewValues = useCallback(
    async (fields: {
      make?: string;
      model?: string;
      variant?: string;
      category?: string;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const products: Record<string, any> = { ...(config?.products || {}) };
      const saved: string[] = [];

      if (fields.make) {
        const currentMakes: Array<{ name: string } | string> =
          products.makes || [];
        const makeNames = currentMakes.map((m) =>
          typeof m === "string" ? m : m.name,
        );
        if (!makeNames.includes(fields.make)) {
          products.makes = [
            ...currentMakes,
            {
              id: `make-${Date.now()}`,
              name: fields.make,
              categories: [],
            },
          ];
          saved.push(`Make: ${fields.make}`);
        }
      }

      if (fields.model) {
        const currentModels: unknown[] = products.models || [];
        const entry = fields.make
          ? { make: fields.make, model: fields.model }
          : fields.model;
        products.models = [...currentModels, entry];
        saved.push(`Model: ${fields.model}`);
      }

      if (fields.variant) {
        const currentVariants: Array<{ name: string } | string> =
          products.variants || [];
        const variantNames = currentVariants.map((v) =>
          typeof v === "string" ? v : v.name,
        );
        if (!variantNames.includes(fields.variant)) {
          products.variants = [
            ...currentVariants,
            {
              id: `var-${Date.now()}`,
              name: fields.variant,
            },
          ];
          saved.push(`Variant: ${fields.variant}`);
        }
      }

      if (fields.category) {
        const currentCats: Array<{ value: string; label: string }> =
          products.asset_categories || [];
        const catValue = fields.category.toLowerCase().replace(/\s+/g, "_");
        const exists = currentCats.some((c) => c.value === catValue);
        if (!exists) {
          products.asset_categories = [
            ...currentCats,
            { value: catValue, label: fields.category },
          ];
          saved.push(`Category: ${fields.category}`);
        }
      }

      if (saved.length === 0) return;

      try {
        await apiClient.put("/api/v1/settings/sections/products", {
          data: products,
        });
        qc.invalidateQueries({ queryKey: ["org-config"] });
        toast.success("Settings saved", saved.join(", "));
      } catch {
        toast.error("Failed to save to settings");
      }
    },
    [config, qc],
  );

  // Custom field values state.
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string>
  >(initialCustomFields || {});

  /**
   * Compose a name from structured fields following the naming convention.
   */
  const composeName = useCallback(
    (y: string, m: string, mod: string, v: string, cat: string): string => {
      const builtinMap: Record<string, string> = {
        year: y,
        make: m,
        model: mod,
        variant: v,
        category: cat,
      };
      return convention.components
        .map((c) =>
          c.builtin ? builtinMap[c.key] || "" : customFieldValues[c.key] || "",
        )
        .filter(Boolean)
        .join(convention.separator || " ")
        .trim();
    },
    [convention, customFieldValues],
  );

  useEffect(() => {
    if (!isStructured) return;
    const composed = composeName(year, make, model, variant, categoryLabel);
    if (composed && composed !== value) {
      onChange(composed);
    }
    if (onFieldsChange) {
      onFieldsChange({
        year: year || undefined,
        make: make || undefined,
        model: model || undefined,
        variant: variant || undefined,
      });
    }
    if (onCustomFieldsChange) {
      const nonEmpty = Object.fromEntries(
        Object.entries(customFieldValues).filter(([, v]) => v),
      );
      if (Object.keys(nonEmpty).length > 0) onCustomFieldsChange(nonEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    year,
    make,
    model,
    variant,
    categoryLabel,
    customFieldValues,
    isStructured,
  ]);

  useEffect(() => {
    if (initialized) return;
    if (initialYear) setYear(initialYear);
    if (initialMake) setMake(initialMake);
    if (initialModel) setModel(initialModel);
    if (initialVariant) setVariant(initialVariant);
    if (initialCustomFields) setCustomFieldValues(initialCustomFields);
    setInitialized(true);
  }, [
    initialYear,
    initialMake,
    initialModel,
    initialVariant,
    initialCustomFields,
    initialized,
  ]);

  const switchToStructured = () => {
    if (value && !year && !make && !model) {
      const assignments = assignTokensToSlots(
        value,
        knownMakes,
        knownVariants,
        categoryLabels,
        convention,
      );
      const newCustom: Record<string, string> = {};
      for (const a of assignments) {
        if (a.slot === "year") setYear(a.value);
        else if (a.slot === "make") setMake(a.value);
        else if (a.slot === "model") setModel(a.value);
        else if (a.slot === "variant") setVariant(a.value);
        else if (a.slot === "category" && onCategoryChange) {
          const catVal = getCategoryValue(allCategories, a.value);
          if (catVal) onCategoryChange(catVal);
        } else if (!BUILTIN_KEYS.has(a.slot)) {
          newCustom[a.slot] = a.value;
        }
      }
      if (Object.keys(newCustom).length > 0) {
        setCustomFieldValues((prev) => ({ ...prev, ...newCustom }));
      }
    }
    setIsStructured(true);
  };

  const switchToFreeForm = () => {
    const composed = composeName(year, make, model, variant, categoryLabel);
    if (composed) onChange(composed);
    setIsStructured(false);
  };

  const inputClass = compact
    ? "h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
    : "h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]";

  const keys = getComponentKeys(convention);
  const hasVariantInConvention = keys.includes("variant");
  const hasCategoryInConvention = keys.includes("category");
  const tailFieldCount =
    (hasVariantInConvention ? 1 : 0) + (hasCategoryInConvention ? 1 : 0);

  // --- Free-form mode with live tokenizer ---
  if (!isStructured) {
    return (
      <div>
        {!compact && (
          <div className="flex items-center gap-1.5 mb-1">
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
              Asset Name
            </label>
            <button
              type="button"
              onClick={switchToStructured}
              title="Switch to structured fields (Year / Make / Model)"
              className="flex items-center gap-1 text-[10px] text-brand-600 hover:text-brand-700 font-medium ml-auto"
            >
              <Wand2 className="h-3 w-3" /> Structured
            </button>
          </div>
        )}
        <div className={compact ? "relative" : ""}>
          <TokenizedNameInput
            value={value}
            onChange={onChange}
            knownMakes={knownMakes}
            knownModels={knownModels}
            knownVariants={knownVariants}
            categoryLabels={categoryLabels}
            allCategories={allCategories}
            convention={convention}
            onApply={(fields) => {
              if (fields.year) setYear(fields.year);
              if (fields.make) setMake(fields.make);
              if (fields.model) setModel(fields.model);
              if (fields.variant) setVariant(fields.variant);
              if (fields.customFields) {
                setCustomFieldValues((prev) => ({
                  ...prev,
                  ...fields.customFields,
                }));
                if (onCustomFieldsChange)
                  onCustomFieldsChange(fields.customFields);
              }

              if (onFieldsChange) {
                onFieldsChange({
                  year: fields.year,
                  make: fields.make,
                  model: fields.model,
                  variant: fields.variant,
                });
              }
              if (fields.category && onCategoryChange) {
                onCategoryChange(fields.category);
              }

              const toSave: {
                make?: string;
                model?: string;
                variant?: string;
                category?: string;
              } = {};
              if (fields.make && !knownMakes.includes(fields.make))
                toSave.make = fields.make;
              if (fields.model) toSave.model = fields.model;
              if (fields.variant && !knownVariants.includes(fields.variant))
                toSave.variant = fields.variant;
              if (
                fields.category &&
                !categoryLabels.some(
                  (c) => c.toLowerCase() === fields.category!.toLowerCase(),
                )
              )
                toSave.category = fields.category;
              if (Object.keys(toSave).length > 0) {
                saveNewValues(toSave);
              }
            }}
            placeholder={placeholder || defaultPlaceholder}
            inputClass={inputClass}
          />
          {compact && (
            <button
              type="button"
              onClick={switchToStructured}
              title="Switch to structured fields"
              className="absolute top-0.5 right-1 p-0.5 text-brand-500 hover:text-brand-700"
            >
              <Wand2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- Structured mode ---
  // Calculate grid columns based on active convention components.
  const structuredFields = convention.components;
  const makeColSpan = tailFieldCount >= 2 ? 3 : 4;
  const modelColSpan = tailFieldCount >= 2 ? 3 : tailFieldCount === 1 ? 4 : 6;

  /** Get the grid col-span for a component based on its type and position. */
  const getColSpan = (comp: NamingComponent): string => {
    if (compact) return "";
    switch (comp.key) {
      case "year":
        return "col-span-2";
      case "make":
        return `col-span-${makeColSpan}`;
      case "model":
        return `col-span-${modelColSpan}`;
      case "variant":
      case "category":
        return "col-span-2";
      default:
        return "col-span-2";
    }
  };

  /** Render a single structured field for a given component. */
  const renderStructuredField = (comp: NamingComponent) => {
    const label = getSlotLabel(comp.key, convention);

    // Built-in: year
    if (comp.key === "year" && comp.builtin) {
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <input
            type="number"
            min="1900"
            max="2099"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder={label}
            className={`block w-full ${inputClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
          />
        </div>
      );
    }

    // Built-in: make
    if (comp.key === "make" && comp.builtin) {
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <ComboSelect
            options={makeOptions}
            value={make}
            onChange={setMake}
            placeholder={label}
            creatable
            onCreate={(val) => {
              setMake(val);
              saveNewValues({ make: val });
            }}
          />
        </div>
      );
    }

    // Built-in: model
    if (comp.key === "model" && comp.builtin) {
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <ComboSelect
            options={modelOptions}
            value={model}
            onChange={setModel}
            placeholder={label}
            creatable
            onCreate={(val) => {
              setModel(val);
              saveNewValues({ make, model: val });
            }}
          />
        </div>
      );
    }

    // Built-in: variant
    if (comp.key === "variant" && comp.builtin) {
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <ComboSelect
            options={variantOptions}
            value={variant}
            onChange={setVariant}
            placeholder={label}
            creatable
            onCreate={(val) => {
              setVariant(val);
              saveNewValues({ variant: val });
            }}
          />
        </div>
      );
    }

    // Built-in: category
    if (comp.key === "category" && comp.builtin) {
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <ComboSelect
            options={allCategories.map((c) => ({
              value: c.value,
              label: c.label,
            }))}
            value={category || ""}
            onChange={(val) => onCategoryChange?.(val)}
            placeholder={label}
            creatable
            onCreate={(val) => {
              const catValue = val.toLowerCase().replace(/\s+/g, "_");
              onCategoryChange?.(catValue);
              saveNewValues({ category: val });
            }}
          />
        </div>
      );
    }

    // Custom component: select
    if (comp.input_type === "select" && comp.options) {
      const opts: ComboOption[] = comp.options.map((o) => ({
        value: o,
        label: o,
      }));
      return (
        <div key={comp.key} className={getColSpan(comp)}>
          {!compact && (
            <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
              {label}
            </label>
          )}
          <ComboSelect
            options={opts}
            value={customFieldValues[comp.key] || ""}
            onChange={(val) =>
              setCustomFieldValues((prev) => ({ ...prev, [comp.key]: val }))
            }
            placeholder={label}
          />
        </div>
      );
    }

    // Custom component: text (default)
    return (
      <div key={comp.key} className={getColSpan(comp)}>
        {!compact && (
          <label className="block text-[9px] text-[var(--text-tertiary)] mb-0.5">
            {label}
          </label>
        )}
        <input
          type="text"
          value={customFieldValues[comp.key] || ""}
          onChange={(e) =>
            setCustomFieldValues((prev) => ({
              ...prev,
              [comp.key]: e.target.value,
            }))
          }
          placeholder={label}
          className={`block w-full ${inputClass}`}
        />
      </div>
    );
  };

  const hasAnyValue =
    year ||
    make ||
    model ||
    variant ||
    category ||
    Object.values(customFieldValues).some(Boolean);

  return (
    <div>
      {!compact && (
        <div className="flex items-center gap-1.5 mb-1">
          <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
            Asset Name
          </label>
          <button
            type="button"
            onClick={switchToFreeForm}
            title="Switch to free-form text entry"
            className="flex items-center gap-1 text-[10px] text-brand-600 hover:text-brand-700 font-medium ml-auto"
          >
            <PenLine className="h-3 w-3" /> Free-form
          </button>
        </div>
      )}

      {compact && (
        <div className="flex justify-end mb-0.5">
          <button
            type="button"
            onClick={switchToFreeForm}
            title="Switch to free-form text entry"
            className="p-0.5 text-brand-500 hover:text-brand-700"
          >
            <PenLine className="h-3 w-3" />
          </button>
        </div>
      )}
      <div
        className={`grid gap-2 ${compact ? `grid-cols-${structuredFields.length}` : "grid-cols-12"}`}
      >
        {structuredFields.map((comp) => renderStructuredField(comp))}
      </div>

      {hasAnyValue && (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[10px] text-[var(--text-tertiary)]">Name:</span>
          <span className="text-xs font-medium text-[var(--text-primary)]">
            {composeName(year, make, model, variant, categoryLabel) || "—"}
          </span>
        </div>
      )}
    </div>
  );
}

export default AssetNameBuilder;
